import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        setErr("This dashboard is admin-only. Use the SND Secure Browser to log in.");
        return;
      }
      nav("/");
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] text-zinc-100 flex">
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 bg-gradient-to-br from-emerald-950/40 via-[#0b0c10] to-[#0b0c10] border-r border-zinc-800">
        <div>
          <div className="text-[11px] tracking-[0.3em] text-emerald-400/80">SND</div>
          <div className="text-3xl font-semibold tracking-tight mt-1">Secure Browser</div>
          <div className="text-sm text-zinc-500 mt-1">Admin Console</div>
        </div>
        <div className="space-y-6 max-w-md">
          <div>
            <div className="text-emerald-400 text-xs tracking-widest mb-2">CONTROLLED · TRACKED · ENCRYPTED</div>
            <h2 className="text-2xl font-medium leading-snug">A locked-down browser, built for your workplace.</h2>
            <p className="text-sm text-zinc-500 mt-3">Manage users, per-user policies, password vaults and URL history from one console.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs text-zinc-400">
            <div className="rounded-md border border-zinc-800 p-3"><div className="text-emerald-400 mb-1">Per-user policies</div>Block downloads, prints, screenshots & more</div>
            <div className="rounded-md border border-zinc-800 p-3"><div className="text-emerald-400 mb-1">Password vault</div>Autofill without exposing values to user</div>
            <div className="rounded-md border border-zinc-800 p-3"><div className="text-emerald-400 mb-1">URL tracking</div>Background log of every visited URL</div>
            <div className="rounded-md border border-zinc-800 p-3"><div className="text-emerald-400 mb-1">Site blacklist</div>Block sites per user, instantly</div>
          </div>
        </div>
        <div className="text-xs text-zinc-600">© SND · v1.0</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <div className="text-xs tracking-widest text-emerald-400/80">SIGN IN</div>
            <h1 className="text-2xl font-semibold mt-1">Admin access</h1>
            <p className="text-sm text-zinc-500 mt-1">Use your admin credentials to continue.</p>
          </div>
          <div>
            <label className="text-xs text-zinc-400">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 focus:border-emerald-500/60 outline-none text-sm"
              placeholder="akshay@snd.in"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400">Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 focus:border-emerald-500/60 outline-none text-sm"
              placeholder="••••••••"
            />
          </div>
          {err && <div data-testid="login-error" className="text-sm text-red-400">{err}</div>}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-medium disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-xs text-zinc-500 pt-2">
            End users sign in directly inside the SND Secure Browser desktop app.
          </div>
        </form>
      </div>
    </div>
  );
}
